import { type ReactNode } from 'react';
import { ErrorState, Skeleton } from './ui';

/* ------------------------------------------------------------------ *
 *  Liste ekranlarinin ortak tablosu.
 *
 *  Genis tablolar kendi kutusunda yatay kayar; sayfa govdesi kaymaz.
 * ------------------------------------------------------------------ */

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Hucre icerigi. */
  cell: (row: T) => ReactNode;
  /** Sag hizali (tutar, adet) sutunlar icin. */
  align?: 'left' | 'right' | 'center';
  className?: string;
  /** Dar ekranda gizlenecek ikincil sutunlar. */
  hideOnMobile?: boolean;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  isLoading,
  error,
  onRetry,
  emptyMessage = 'Kayıt bulunamadı.',
  onRowClick,
  selectedKeys,
  onToggleSelect,
  onToggleSelectAll,
  footer,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  selectedKeys?: Set<string | number>;
  onToggleSelect?: (key: string | number) => void;
  onToggleSelectAll?: (checked: boolean) => void;
  footer?: ReactNode;
}) {
  if (error) {
    return <ErrorState error={error} onRetry={onRetry} />;
  }

  const selectable = selectedKeys !== undefined && onToggleSelect !== undefined;
  const allSelected = selectable && rows.length > 0 && rows.every((row) => selectedKeys!.has(rowKey(row)));

  return (
    <div className="overflow-hidden rounded-xl border border-ink-200 bg-white">
      <div className="scroll-x">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-ink-200 bg-ink-50/70">
              {selectable ? (
                <th className="w-10 px-3 py-2.5">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-ink-300 text-brand-600"
                    checked={allSelected}
                    onChange={(event) => onToggleSelectAll?.(event.target.checked)}
                    aria-label="Tümünü seç"
                  />
                </th>
              ) : null}

              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-ink-500 ${
                    column.align === 'right'
                      ? 'text-right'
                      : column.align === 'center'
                        ? 'text-center'
                        : 'text-left'
                  } ${column.hideOnMobile ? 'hidden md:table-cell' : ''} ${column.className ?? ''}`}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-ink-100">
            {isLoading ? (
              Array.from({ length: 6 }, (_, index) => (
                <tr key={index}>
                  {selectable ? <td className="px-3 py-3" /> : null}
                  {columns.map((column) => (
                    <td key={column.key} className="px-3 py-3">
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="px-3 py-12 text-center text-sm text-ink-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const key = rowKey(row);
                return (
                  <tr
                    key={key}
                    className={`transition hover:bg-ink-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {selectable ? (
                      <td className="px-3 py-3" onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-ink-300 text-brand-600"
                          checked={selectedKeys!.has(key)}
                          onChange={() => onToggleSelect!(key)}
                          aria-label="Satırı seç"
                        />
                      </td>
                    ) : null}

                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-3 py-3 align-middle ${
                          column.align === 'right'
                            ? 'text-right'
                            : column.align === 'center'
                              ? 'text-center'
                              : 'text-left'
                        } ${column.hideOnMobile ? 'hidden md:table-cell' : ''}`}
                      >
                        {column.cell(row)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {footer ? <div className="border-t border-ink-200 px-4 py-3">{footer}</div> : null}
    </div>
  );
}

/** Liste ustundeki arama ve filtre seridi. */
export function Toolbar({ children }: { children: ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-ink-200 bg-white p-3.5">
      {children}
    </div>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Ara…',
  className = 'min-w-56 flex-1',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={`relative ${className}`}>
      <label className="sr-only" htmlFor="table-search">
        {placeholder}
      </label>
      <input
        id="table-search"
        type="search"
        className="field py-2 pl-9 text-sm"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <svg
        viewBox="0 0 20 20"
        className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-ink-400"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
      >
        <circle cx="9" cy="9" r="6" />
        <path d="M13.5 13.5L17 17" strokeLinecap="round" />
      </svg>
    </div>
  );
}

export function SelectFilter({
  label,
  value,
  onChange,
  options,
  allLabel = 'Tümü',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  allLabel?: string;
}) {
  const id = `filter-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="field w-auto min-w-36 py-2 text-sm"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{allLabel}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Ozet kart: pano ve liste ustlerinde sayisal gostergeler. */
export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'neutral' | 'positive' | 'negative';
}) {
  const toneClass =
    tone === 'positive' ? 'text-emerald-700' : tone === 'negative' ? 'text-red-600' : 'text-ink-900';

  return (
    <div className="rounded-xl border border-ink-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</p>
      <p className={`mt-1.5 text-2xl font-semibold ${toneClass}`}>{value}</p>
      {hint ? <p className="mt-1 text-xs text-ink-400">{hint}</p> : null}
    </div>
  );
}
